import React, { Component } from "react";
import { Link } from "react-router-dom";
import { setLanguage, translate } from "react-multi-lang";
import configuration from "react-global-configuration";
import { withToastManager } from "react-toast-notifications";
import BrandLinksRow from "../../Helper/BrandLinksRow";
import { normalizeExternalUrl } from "../../Helper/urls";
import { notifyShellLoadError } from "../../Helper/toaster";
import {
  fetchFooterNavigation,
  isExternalFooterUrl,
  FOOTER_ENTITY_LANDING_ITEMS,
  sanitizeFooterSections,
} from "../footerApi";

/**
 * Public footer — Footer Manager columns plus entity landings.
 * Empty/unpublished managed content ⇒ empty-safe fallback (Orgs/Creators/Projects).
 * Contact Us and Discover are not rendered. Language switcher stays hidden.
 */

const SHOW_FOOTER_LANGUAGE = false;
class Footer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isAuthenticated: this.props.data,
            loading: true,
            managedSections: [],
            useManagedFooter: false,
        };
    }

    componentDidMount() {
        fetchFooterNavigation()
            .then((payload) => {
                const sections = (payload && payload.sections) || [];
                this.setState({
                    loading: false,
                    useManagedFooter: !!(payload && payload.managed && !payload.fallback),
                    managedSections: sections,
                });
                if (payload && payload.loadError) {
                    notifyShellLoadError(this.props.toastManager, "footer");
                }
            })
            .catch(() => {
                this.setState({
                    loading: false,
                    useManagedFooter: false,
                    managedSections: [],
                });
                notifyShellLoadError(this.props.toastManager, "footer");
            });
    }

    handleChangeLang = ({ currentTarget: input }) => {
        setLanguage(input.value);
        localStorage.setItem("lang", input.value);
        window.location.reload();
    };

    renderFooterLink(item, key) {
        const url = item.url || "#";
        const title = item.title || "";
        const children = Array.isArray(item.children) ? item.children : [];

        if (item.link_type === "group" || children.length > 0) {
            return (
                <li className="footer-link-list footer-link-group" key={key}>
                    <span className="footer-group-title">{title}</span>
                    {children.length > 0 ? (
                        <ul className="footer-link-nested">
                            {children.map((child, cIdx) =>
                                this.renderFooterLink(child, `${key}-c-${child.id || cIdx}`)
                            )}
                        </ul>
                    ) : null}
                </li>
            );
        }

        if (isExternalFooterUrl(url) || item.link_type === "external") {
            return (
                <li className="footer-link-list" key={key}>
                    <a
                        href={normalizeExternalUrl(url)}
                        target={item.open_in_new_tab ? "_blank" : undefined}
                        rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}
                    >
                        {title}
                    </a>
                </li>
            );
        }
        return (
            <li className="footer-link-list" key={key}>
                <Link to={url}>{title}</Link>
            </li>
        );
    }

    /**
     * Ensure Organizations / Creators / Projects stay reachable even when
     * Footer Manager is publishing other columns without those landings.
     * Never inject a Discover heading.
     */
    sectionsWithEntityLandings() {
        const sections = sanitizeFooterSections(
            Array.isArray(this.state.managedSections)
                ? this.state.managedSections.slice()
                : []
        );
        const hasEntityLandings = sections.some((section) => {
            const items = (section && section.items) || [];
            const urls = items.map((i) => (i && i.url) || "");
            return (
                urls.indexOf("/organizations") >= 0 &&
                urls.indexOf("/creators") >= 0 &&
                urls.indexOf("/projects") >= 0
            );
        });
        if (hasEntityLandings) {
            return sections;
        }
        return [
            {
                key: "entity_landings",
                column_key: "entity_landings",
                title: "",
                items: FOOTER_ENTITY_LANDING_ITEMS.slice(),
            },
        ].concat(sections);
    }

    renderColumns() {
        const sections = this.sectionsWithEntityLandings();
        if (!sections.length) {
            return null;
        }
        const colClass =
            sections.length >= 4
                ? "col-xs-12 col-sm-6 col-md-3 col-lg-2"
                : sections.length === 3
                ? "col-xs-12 col-sm-6 col-md-3 col-lg-3"
                : "col-xs-12 col-md-4 col-lg-3 col-xl-3";

        return sections.map((section, sIdx) => (
            <div
                className={colClass}
                key={section.id || section.column_key || section.key || `section-${sIdx}`}
                data-column-key={section.column_key || section.key || undefined}
            >
                {section.title ? (
                    <p className="footer-head">{section.title}</p>
                ) : null}
                <ul className="footer-link">
                    {(section.items || []).map((item, iIdx) =>
                        this.renderFooterLink(item, `m-${sIdx}-${item.id || iIdx}`)
                    )}
                </ul>
            </div>
        ));
    }

    render() {
        const { t } = this.props;
        return (
            <div className="main-footer-sec-content">
                <div className="bottom-height"></div>
                <div className="footer" id="footer">
                    <div className="site-footer">
                        <div className="row">
                            {this.state.loading ? null : this.renderColumns()}
                            <div className="col-xs-12 col-md-8 col-lg-6 col-xl-6">
                                <div className="row">
                                    <div className="col-xs-12 col-md-6 col-lg-6 col-xl-6">
                                        <p className="footer-head">{t("get_app")}</p>
                                        <a
                                            href={normalizeExternalUrl(
                                                configuration.get("configData.appstore")
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img
                                                src="/assets/img/app-store.png"
                                                className="app-img resp-marg-right-xs"
                                                alt="Playstore"
                                            />
                                        </a>
                                        <a
                                            href={normalizeExternalUrl(
                                                configuration.get("configData.playstore")
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img
                                                src="/assets/img/play-store.png"
                                                className="app-img"
                                                alt="Playstore"
                                            />
                                        </a>
                                    </div>
                                    <div className="col-sm-6 col-md-6 col-lg-6 col-xl-6">
                                        <p className="footer-head">{t("find_us")}</p>
                                        <div className="social-share">
                                            <BrandLinksRow placement="footer" className="" />
                                        </div>
                                        {SHOW_FOOTER_LANGUAGE ? (
                                            <React.Fragment>
                                                <hr></hr>
                                                <div className="select-lang-drop-down">
                                                    <select
                                                        className="form-control language-select mb-3"
                                                        onChange={this.handleChangeLang}
                                                        name="lang"
                                                        defaultValue={
                                                            localStorage.getItem("lang") || "en"
                                                        }
                                                    >
                                                        <option value="en">{t("english")}</option>
                                                        <option value="pt">{t("spanish")}</option>
                                                    </select>
                                                </div>
                                            </React.Fragment>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/*
                            Build fingerprint stays machine-readable only
                            (window.__TPG_BUILD__, <meta name="tpg-build">,
                            /build-info.json, /health/frontend). It is never
                            rendered to end users.
                        */}
                        <p className="footer-bottom">
                            {configuration.get("configData.site_name")}
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

export default withToastManager(translate(Footer));
